import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Album, AlbumItem, SaveToMoodboardPayload } from '@/types/moodboards';

export function useAlbums(userId?: string) {
  return useQuery({
    queryKey: ['albums', userId],
    queryFn: async () => {
      let query = supabase
        .from('albums')
        .select('*, album_items(count)')
        .order('updated_at', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map(a => ({
        ...a,
        item_count: a.album_items?.[0]?.count || 0,
        album_items: undefined
      })) as Album[];
    },
    enabled: !!userId,
  });
}

export function useAlbum(albumId?: string) {
  return useQuery({
    queryKey: ['album', albumId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('albums')
        .select('*')
        .eq('id', albumId!)
        .single();

      if (error) throw error;
      return data as Album;
    },
    enabled: !!albumId,
  });
}

export function useAlbumItems(albumId?: string) {
  return useQuery({
    queryKey: ['album-items', albumId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('album_items')
        .select('*')
        .eq('album_id', albumId!)
        .order('position', { ascending: true });

      if (error) throw error;
      return data as AlbumItem[];
    },
    enabled: !!albumId,
  });
}

export function useCreateAlbum() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      name: string;
      is_pinned?: boolean;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('albums')
        .insert({
          user_id: user.id,
          name: params.name,
          is_pinned: params.is_pinned || false,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Album;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['albums'] });
      toast({
        title: 'Album created',
        description: 'Your new album is ready!',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create album',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateAlbum() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      name?: string;
      is_pinned?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('albums')
        .update({
          name: params.name,
          is_pinned: params.is_pinned,
        })
        .eq('id', params.id)
        .select()
        .single();

      if (error) throw error;
      return data as Album;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['albums'] });
      queryClient.invalidateQueries({ queryKey: ['album', data.id] });
      toast({
        title: 'Album updated',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update album',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteAlbum() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (albumId: string) => {
      const { error } = await supabase
        .from('albums')
        .delete()
        .eq('id', albumId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['albums'] });
      toast({
        title: 'Album deleted',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete album',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useAddToAlbum() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      albumId: string;
      item: SaveToMoodboardPayload;
    }) => {
      // Verify the user owns the content
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check ownership based on content type
      let isOwner = false;
      if (params.item.content_type === 'bulletin') {
        const { data } = await supabase
          .from('bulletins')
          .select('user_id')
          .eq('id', params.item.content_id)
          .single();
        isOwner = data?.user_id === user.id;
      } else if (params.item.content_type === 'loop') {
        const { data } = await supabase
          .from('loops')
          .select('user_id')
          .eq('id', params.item.content_id)
          .single();
        isOwner = data?.user_id === user.id;
      }

      if (!isOwner) {
        throw new Error('You can only add your own content to albums');
      }

      // Get the current max position
      const { data: existingItems } = await supabase
        .from('album_items')
        .select('position')
        .eq('album_id', params.albumId)
        .order('position', { ascending: false })
        .limit(1);

      const newPosition = existingItems && existingItems.length > 0 
        ? existingItems[0].position + 1 
        : 0;

      // Check if item already exists
      const { data: existing } = await supabase
        .from('album_items')
        .select('id')
        .eq('album_id', params.albumId)
        .eq('content_type', params.item.content_type)
        .eq('content_id', params.item.content_id)
        .maybeSingle();

      if (existing) {
        toast({
          title: 'Already in album',
          description: 'This item is already in this album',
        });
        return null;
      }

      const { data, error } = await supabase
        .from('album_items')
        .insert({
          album_id: params.albumId,
          content_type: params.item.content_type,
          content_id: params.item.content_id,
          position: newPosition,
          image_url: params.item.image_url,
          caption: params.item.caption,
        })
        .select()
        .single();

      if (error) throw error;

      // Update album's updated_at
      await supabase
        .from('albums')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', params.albumId);

      // Update cover if first item
      if (newPosition === 0) {
        await supabase
          .from('albums')
          .update({ cover_image_url: params.item.image_url })
          .eq('id', params.albumId);
      }

      return data as AlbumItem;
    },
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['album-items', variables.albumId] });
        queryClient.invalidateQueries({ queryKey: ['albums'] });
        toast({
          title: 'Added to album',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to add to album',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRemoveFromAlbum() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { itemId: string; albumId: string }) => {
      const { error } = await supabase
        .from('album_items')
        .delete()
        .eq('id', params.itemId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['album-items', variables.albumId] });
      queryClient.invalidateQueries({ queryKey: ['albums'] });
      toast({
        title: 'Removed from album',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove from album',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useReorderAlbumItems() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      albumId: string;
      items: { id: string; position: number }[];
    }) => {
      const updates = params.items.map(item =>
        supabase
          .from('album_items')
          .update({ position: item.position })
          .eq('id', item.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      
      if (errors.length > 0) {
        throw new Error('Failed to reorder items');
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['album-items', variables.albumId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to reorder items',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
